import keyBy from 'lodash/keyBy';
import { selectRow, selectRowByRowIndex } from '@client-shared/ui-agGrid';

import { fetchActionFactory } from '../../../shared/store/fetchFactory';

import { fetchingError } from '../../../shared/store/actions';
import { errorNotification, infoNotification, successNotification } from '../../../shared/store/notificationActions';

import {
  FETCH_OBLIGATIONS,
  FETCH_UPLOADED_OBLIGATIONS,
  FETCH_LOOKUP_DATA_FOR_OBLIGATIONS,
  FETCH_LOOKUP_DATA_FOR_OBLIGATIONS_SUCCESS,
  FETCH_LOOKUP_DATA_FOR_OBLIGATIONS_FAILURE,
  UPDATE_OBLIGATION,
  UPDATE_OBLIGATION_SUCCESS,
  UPDATE_OBLIGATION_FAILURE,
  CREATE_OBLIGATION,
  CREATE_OBLIGATION_SUCCESS,
  CREATE_OBLIGATION_FAILURE,
  IMPORT_OBLIGATIONS,
  IMPORT_OBLIGATIONS_FAILURE,
  IMPORT_OBLIGATIONS_SUCCESS,
  CLEAR_UPLOADED_OBLIGATIONS,
  DELETE_OBLIGATION,
  DELETE_OBLIGATION_SUCCESS,
  DELETE_OBLIGATION_FAILURE,
  FETCH_USER_ASSIGNED_COMPANY_CODES,
} from './types';

import {
  fetchObligations as fetchObligationsApi,
  fetchUploadedObligations as fetchUploadedObligationsApi,
  fetchUserAssignedCompanyCodes as fetchUserAssignedCompanyCodesApi,
  fetchCharts as fetchChartsApi,
  fetchUsTaxTypes as fetchUsTaxTypesApi,
  fetchLocalTaxTypes as fetchLocalTaxTypesApi,
  fetchCurrencyCodes as fetchCurrencyCodesApi,
  fetchCompanyCodes as fetchCompanyCodesApi,
  fetchCountryTaxTypes as fetchCountryTaxTypesApi,
  updateObligation as updateObligationApi,
  createObligation as createObligationApi,
  fetchTaxCurrencyDefaultCodes as fetchTaxCurrencyDefaultCodesApi,
  removeObligation as removeObligationApi,
  fetchFilingGroups as fetchFilingGroupsApi,
} from '../api';

import {
  fetchConnections as fetchConnectionsApi,
} from '../../../shared/api';

import agGridConfig from '../../../agGridConfig';

import { encodeCountryTaxType } from '../../../utils/encodeDecodeCTT';

import { addJob } from '../../../shared/jim/store/actions';

import Config from '../../../config';
import getModalActions from '../../../shared/modals/getModalActions';
import { ModalNames } from '../constants';

export const {
  showAction: showObligationModalForDelete,
  hideAction: hideObligationModalForDelete,
} = getModalActions(ModalNames.obligationDeleteModal);

export const fetchObligations = fetchActionFactory(
  FETCH_OBLIGATIONS,
  fetchObligationsApi,
  { field: 'obligations' }
);

export const fetchUploadedObligations = fetchActionFactory(
  FETCH_UPLOADED_OBLIGATIONS,
  fetchUploadedObligationsApi,
  { field: 'uploadedObligations' }
);

export const fetchUserAssignedCompanyCodes = fetchActionFactory(
  FETCH_USER_ASSIGNED_COMPANY_CODES,
  fetchUserAssignedCompanyCodesApi,
  { field: 'userAssignedCompanyCodes' }
);

export const fetchLookUpDataForObligations = periodName => async dispatch => {
  dispatch({ type: FETCH_LOOKUP_DATA_FOR_OBLIGATIONS });
  try {
    const [
      chartNames,
      usTaxTypeValues,
      localTaxTypeValues,
      currencyCodes,
      connections,
      companyCodes,
      countryTaxTypes,
      taxCurrencyDefaultCodes,
      filingGroups,
    ] = await Promise.all([
      fetchChartsApi(periodName),
      fetchUsTaxTypesApi(),
      fetchLocalTaxTypesApi(),
      fetchCurrencyCodesApi(),
      fetchConnectionsApi({ period: periodName }),
      fetchCompanyCodesApi(periodName),
      fetchCountryTaxTypesApi(periodName),
      fetchTaxCurrencyDefaultCodesApi(),
      fetchFilingGroupsApi(periodName),
    ]);

    return dispatch({
      type: FETCH_LOOKUP_DATA_FOR_OBLIGATIONS_SUCCESS,
      lookUpData: {
        chartNameOptions: chartNames.map(({ chartName }) => ({ value: chartName, label: chartName })),
        usTaxTypeOptions: usTaxTypeValues.map(({ name, value }) => ({ label: name, value })),
        localTaxTypeOptions: localTaxTypeValues.map(({ name, value }) => ({ label: name, value })),
        currencyCodeOptions: currencyCodes.map(({ currencyCode }) => ({ label: currencyCode, value: currencyCode })),
        connectionOptions: connections.map(({ connName }) => ({ value: connName, label: connName })),
        companyCodeOptions: companyCodes.map(
          ({
            companyCode,
            ccDescription,
            subledgerFlag,
            statCalc,
            associatedLeCountryCode,
            enabledFlag,
            connName,
            legalEntity,
            chartName,
            functionalCurrency,
            statCurrency,
            recordFTC,
            secondaryTax,
            homeOfficeCompanyCode,
          }) => ({
            value: companyCode,
            label: `${companyCode} - ${ccDescription}`,
            subledgerFlag,
            statCalc,
            associatedLeCountryCode,
            enabledFlag,
            connName,
            legalEntity,
            chartName,
            functionalCurrency,
            statCurrency,
            recordFTC,
            secondaryTax,
            homeOfficeCompanyCode,
          })
        ),
        countryTaxTypesOptions: countryTaxTypes.map(({ countryTaxType, countryCode, description, ...rest }) => ({
          value: encodeCountryTaxType(countryCode, countryTaxType),
          label: `${countryTaxType} - ${description}`,
          description,
          ...rest,
        })
        ),
        taxCurrencyDefaultCodes: keyBy(taxCurrencyDefaultCodes, 'countryCode'),
        filingGroupOptions: filingGroups.map(({ description }) => ({ value: description, label: description })),
      },
    });
  } catch (error) {
    dispatch({ type: FETCH_LOOKUP_DATA_FOR_OBLIGATIONS_FAILURE });
    if (error.isFetchingError) {
      dispatch(fetchingError());
      dispatch(errorNotification(error.message));
      return;
    }
    throw error;
  }
};

export const createObligation = (
  createdRow,
  { periodName },
  { resetEditableGrid }
) => async dispatch => {
  try {
    dispatch({ type: CREATE_OBLIGATION });

    await createObligationApi({
      enabledFlag: createdRow.enabledFlag,
      companyCode: createdRow.companyCode,
      legalEntity: createdRow.legalEntity,
      countryCode: createdRow.countryCode,
      countryTaxType: createdRow.countryTaxType,
      connectionName: createdRow.connectionName,
      chartName: createdRow.chartName,
      usTaxType: createdRow.usTaxType,
      localTaxType: createdRow.localTaxType,
      functionalCurrency: createdRow.functionalCurrency,
      taxCurrency: createdRow.taxCurrency,
      statCurrency: createdRow.statCurrency,
      filingGroup: createdRow.filingGroup,
      secondaryTax: createdRow.secondaryTax,
      homeOfficeCompanyCode: createdRow.homeOfficeCompanyCode,
      recordFTC: createdRow.recordFTC,
      federalFTCLimit: createdRow.federalFTCLimit,
      stateMinimumTax: createdRow.stateMinimumTax,
      stateApportionmentRate: createdRow.stateApportionmentRate,
      statePrePostNOL: createdRow.statePrePostNOL,
      NOL: createdRow.NOL,
      recordVA: createdRow.recordVA,
      deferredTaxRate: createdRow.deferredTaxRate,
      currentTaxRate: createdRow.currentTaxRate,
      comment: createdRow.comment,
    }, { periodName });
    dispatch({ type: CREATE_OBLIGATION_SUCCESS });

    await dispatch(fetchObligations(periodName));

    dispatch(selectRow({
      name: agGridConfig.adminObligations.name,
      fields: agGridConfig.adminObligations.fields,
      data: createdRow,
    }));

    dispatch(successNotification('Obligation created successfully'));
  } catch (error) {
    dispatch({ type: CREATE_OBLIGATION_FAILURE });
    resetEditableGrid();
    if (error.isFetchingError) {
      dispatch(fetchingError());
      dispatch(errorNotification(error.message));
      return;
    }
    throw error;
  }
};

export const updateObligation = (
  editedRow,
  { periodName },
  { resetEditableGrid }
) => async dispatch => {
  try {
    dispatch({ type: UPDATE_OBLIGATION });

    await updateObligationApi({
      enabledFlag: editedRow.enabledFlag,
      companyCode: editedRow.companyCode,
      legalEntity: editedRow.legalEntity,
      countryCode: editedRow.countryCode,
      countryTaxType: editedRow.countryTaxType,
      connectionName: editedRow.connectionName,
      chartName: editedRow.chartName,
      usTaxType: editedRow.usTaxType,
      localTaxType: editedRow.localTaxType,
      functionalCurrency: editedRow.functionalCurrency,
      taxCurrency: editedRow.taxCurrency,
      statCurrency: editedRow.statCurrency,
      filingGroup: editedRow.filingGroup,
      secondaryTax: editedRow.secondaryTax,
      homeOfficeCompanyCode: editedRow.homeOfficeCompanyCode,
      recordFTC: editedRow.recordFTC,
      federalFTCLimit: editedRow.federalFTCLimit,
      stateMinimumTax: editedRow.stateMinimumTax,
      stateApportionmentRate: editedRow.stateApportionmentRate,
      statePrePostNOL: editedRow.statePrePostNOL,
      NOL: editedRow.NOL,
      recordVA: editedRow.recordVA,
      deferredTaxRate: editedRow.deferredTaxRate,
      currentTaxRate: editedRow.currentTaxRate,
      comment: editedRow.comment,
    }, { periodName });
    dispatch({ type: UPDATE_OBLIGATION_SUCCESS });

    await dispatch(fetchObligations(periodName));

    dispatch(selectRow({
      name: agGridConfig.adminObligations.name,
      fields: agGridConfig.adminObligations.fields,
      data: editedRow,
    }));

    dispatch(successNotification('Obligation updated successfully'));
  } catch (error) {
    dispatch({ type: UPDATE_OBLIGATION_FAILURE });
    resetEditableGrid();
    if (error.isFetchingError) {
      dispatch(fetchingError());
      dispatch(errorNotification(error.message));
      return;
    }
    throw error;
  }
};


export const uploadObligations = (file, currentPeriod) =>
  async dispatch => {
    dispatch(infoNotification('Obligations file upload processing...'));

    try {
      await dispatch(
        addJob({
          jobType: Config.jimJobs.obligationsValidate.type,
          payload: {
            file,
            periodName: currentPeriod.name,
          },
        }));
    } catch (error) {
      dispatch(errorNotification(error.message));
    }
  };

export const importObligations = (
  jobId, currentPeriod
) => async dispatch => {
  dispatch({
    type: IMPORT_OBLIGATIONS,
  });
  dispatch(infoNotification('Obligations file import processing...'));
  try {
    await dispatch(addJob({
      jobType: Config.jimJobs.obligationsImport.type,
      payload: {
        jobId,
        periodName: currentPeriod.name,
      },
    }));
  } catch (error) {
    dispatch(errorNotification(error.message));
    dispatch({
      type: IMPORT_OBLIGATIONS_FAILURE,
    });
  }
};

export const importObligationsSuccess = () => dispatch => {
  dispatch({
    type: IMPORT_OBLIGATIONS_SUCCESS,
  });
};

export const importObligationsFailed = () => dispatch => {
  dispatch({
    type: IMPORT_OBLIGATIONS_FAILURE,
  });
};

export const clearUploadedObligations = () => async dispatch =>
  dispatch({
    type: CLEAR_UPLOADED_OBLIGATIONS,
  });

export const removeObligation = (obligation, { rowIndex, clearRangeSelection }) => async dispatch => {
  try {
    dispatch({ type: DELETE_OBLIGATION });
    await removeObligationApi(obligation);

    dispatch(hideObligationModalForDelete());
    await dispatch(fetchObligations(obligation.periodName));
    clearRangeSelection();
    dispatch(selectRowByRowIndex(rowIndex ? rowIndex - 1 : 0));
    dispatch({ type: DELETE_OBLIGATION_SUCCESS });
    dispatch(successNotification('Obligation removed successfully'));
  } catch (error) {
    if (error.isFetchingError) {
      dispatch(fetchingError());
      dispatch(errorNotification(error.message));
      return dispatch({ type: DELETE_OBLIGATION_FAILURE });
    }
    throw error;
  }
};
